import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import {
  BookingPassport,
  BookingPassportEditForm,
  PASSPORT_TYPE_LABEL,
  downloadPassportAttachment,
  editPassportBooking,
  sanitizeFileName,
} from "src/store/reducers/bookings";
import { useAppDispatch } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";

const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";

type IEditPassportBookingCanvas = {
  Title: string;
  editId: string;
  bookingId: string;
  hide: () => void;
  data: BookingPassport;
};

const EditPassportBookingCanvas = ({
  Title,
  editId,
  bookingId,
  hide,
  data,
}: IEditPassportBookingCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [downloading, setDownloading] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!data) {
      hide();
    }
  }, [data, hide]);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    setErrors,
    errors,
  } = useFormik<{
    passport_number: string;
    fullname: string;
    nationality: string;
    date_of_birth: string;
    issue_date: string;
    expiry_date: string;
    issue_country: string;
    place_of_issue: string;
    passport_type: number;
    notes: string;
  }>({
    initialValues: {
      passport_number: data?.passport_number || "",
      fullname: data?.fullname || "",
      nationality: data?.nationality || "",
      date_of_birth: data?.date_of_birth || "",
      issue_date: data?.issue_date || "",
      expiry_date: data?.expiry_date || "",
      issue_country: data?.issue_country || "",
      place_of_issue: data?.place_of_issue || "",
      passport_type: data?.passport_type ?? 1,
      notes: data?.notes || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      passport_number: KeyRequired("Passport number"),
      fullname: KeyRequired("Full name"),
      passport_type: KeyRequired("Passport type"),
      nationality: KeyRequired("Nationality"),
      date_of_birth: Yup.string().required("Date of birth is required"),
      issue_date: Yup.string().required("Issue date is required"),
      expiry_date: Yup.string().required("Expiry date is required"),
      issue_country: KeyRequired("Issue country"),
      place_of_issue: KeyRequired("Place of issue"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      editPassport({ ...values, id: editId, booking: data.booking });
    },
  });

  const editPassport = useCallback(
    async (values: BookingPassportEditForm) => {
      try {
        await dispatch(
          editPassportBooking({ form: values, files: newFiles })
        ).unwrap();
        toast.success("Passport is updated successfully");
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, hide, newFiles, setErrors]
  );

  useEffect(() => {
    resetForm();
    setNewFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [resetForm, editId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const addedFiles = Array.from(e.target.files);
      setNewFiles((prev) => [...prev, ...addedFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownload = useCallback(
    async (documentId: string) => {
      try {
        setDownloading(documentId);
        const url = await dispatch(
          downloadPassportAttachment({
            booking_id: bookingId,
            passport_id: data.id,
            document_id: documentId,
          })
        ).unwrap();
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", documentId);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 200);
      } catch (e) {
        toast.error("Failed to download attachment");
      } finally {
        setDownloading(undefined);
      }
    },
    [bookingId, data, dispatch]
  );

  return (
    <Offcanvas
      show={!!editId}
      onHide={hide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button type="button" className="btn-close" onClick={hide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="fullname"
                  labelText="Full Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.fullname}
                  touched={touched.fullname}
                  errors={errors.fullname}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="passport_number"
                  labelText="Passport Number"
                  required={true}
                  handleChange={handleChange}
                  value={values.passport_number}
                  touched={touched.passport_number}
                  errors={errors.passport_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="nationality"
                  labelText="Nationality"
                  required={true}
                  handleChange={handleChange}
                  value={values.nationality}
                  touched={touched.nationality}
                  errors={errors.nationality}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="date_of_birth"
                  labelText="Date of Birth"
                  inputType="date"
                  required={true}
                  handleChange={handleChange}
                  value={values.date_of_birth}
                  touched={touched.date_of_birth}
                  errors={errors.date_of_birth}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="issue_date"
                  labelText="Issue Date"
                  inputType="date"
                  required={true}
                  handleChange={handleChange}
                  value={values.issue_date}
                  touched={touched.issue_date}
                  errors={errors.issue_date}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="expiry_date"
                  labelText="Expiry Date"
                  inputType="date"
                  required={true}
                  handleChange={handleChange}
                  value={values.expiry_date}
                  touched={touched.expiry_date}
                  errors={errors.expiry_date}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="issue_country"
                  labelText="Issue Country"
                  required={true}
                  handleChange={handleChange}
                  value={values.issue_country}
                  touched={touched.issue_country}
                  errors={errors.issue_country}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="place_of_issue"
                  labelText="Place of Issue"
                  required={true}
                  handleChange={handleChange}
                  value={values.place_of_issue}
                  touched={touched.place_of_issue}
                  errors={errors.place_of_issue}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="passport_type"
                  labelText="Passport Type"
                  required={true}
                  handleChange={handleChange}
                  value={values.passport_type}
                  touched={touched.passport_type}
                  errors={errors.passport_type}
                >
                  {Object.entries(PASSPORT_TYPE_LABEL).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="notes"
                  labelText="Notes"
                  handleChange={handleChange}
                  value={values.notes}
                  touched={touched.notes}
                  errors={errors.notes}
                />
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Existing Attachments</Form.Label>
                {data?.attachments &&
                Object.keys(data.attachments).length > 0 ? (
                  <div className="mb-2">
                    {Object.entries(data.attachments).map(([docName]) => (
                      <div
                        key={docName}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">{docName}</span>
                        <button
                          type="button"
                          className="btn btn-primary btn-xs"
                          disabled={downloading === docName}
                          onClick={() => handleDownload(docName)}
                        >
                          {downloading === docName ? (
                            <i className="fa-solid fa-spinner" />
                          ) : (
                            <i className="fa-solid fa-download" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-2">No existing attachments</p>
                )}
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Add New Attachments</Form.Label>
                <Form.Control
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Accepted formats: JPG, JPEG, PNG, PDF
                </Form.Text>
                {newFiles.length > 0 && (
                  <div className="mt-2">
                    {newFiles.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">
                          {sanitizeFileName(file.name)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removeNewFile(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              Submit
            </button>
            <Link to={"#"} onClick={hide} className="btn btn-danger light ms-1">
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default EditPassportBookingCanvas;
